import React, { useState, useEffect } from 'react';
import { X, DollarSign, User, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

/**
 * 赏金管理弹窗组件
 */
const RewardManagementModal = ({ chartId, paymentAmount, bizType, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [chartInfo, setChartInfo] = useState(null);
  const [comments, setComments] = useState([]);
  const [userGroups, setUserGroups] = useState([]); // 按用户分组的评论
  const [rewardAmounts, setRewardAmounts] = useState({}); // 存储每个用户要分配的金额

  useEffect(() => {
    fetchRewardInfo();
  }, [chartId]);

  const fetchRewardInfo = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      // 根据业务类型调用不同的API
      let apiUrl;
      switch (bizType) {
        case 'liuyao':
          apiUrl = `/api/liuyao/${chartId}/reward-info`;
          break;
        case 'qimen':
          apiUrl = `/api/qimen/${chartId}/reward-info`;
          break;
        case 'bazi':
          apiUrl = `/api/bazi/${chartId}/reward-info`;
          break;
        default:
          throw new Error(`不支持的业务类型: ${bizType}`);
      }

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setChartInfo(response.data.chart);
        setComments(response.data.comments || []);
        
        // 按用户分组评论
        const groups = {};
        (response.data.comments || []).forEach(comment => {
          const userId = comment.user_id;
          if (!groups[userId]) {
            groups[userId] = {
              user_id: userId,
              user_nickname: comment.user_nickname,
              comments: [],
              total_reward: 0,
              is_confirmed: false // 如果任何评论已确认，则用户已确认
            };
          }
          groups[userId].comments.push(comment);
          groups[userId].total_reward += comment.reward_amount || 0;
          if (comment.is_confirmed) {
            groups[userId].is_confirmed = true;
          }
        });
        setUserGroups(Object.values(groups));
        
        // 初始化金额输入 - 按用户
        const amounts = {};
        Object.values(groups).forEach(user => {
          amounts[user.user_id] = user.total_reward || 0;
        });
        setRewardAmounts(amounts);
      }
    } catch (error) {
      console.error('获取赏金信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 计算已分配总额和剩余金额
  const allocatedTotal = Object.values(rewardAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const remainingAmount = paymentAmount - allocatedTotal;
  const isOverLimit = allocatedTotal > paymentAmount;
  const isExactMatch = Math.abs(allocatedTotal - paymentAmount) < 0.01; // 允许0.01的误差
  const isFinished = chartInfo?.is_finished || false; // 悬赏是否已结束

  // 更新某个用户的分配金额
  const handleAmountChange = (userId, value) => {
    const amount = parseFloat(value) || 0;
    setRewardAmounts(prev => ({
      ...prev,
      [userId]: amount
    }));
  };

  // 一次性发放所有赏金
  const handleGrantAllRewards = async () => {
    // 验证分配金额必须等于悬赏总额
    if (!isExactMatch) {
      if (isOverLimit) {
        alert(`分配积分超出悬赏总额 ${(allocatedTotal - paymentAmount).toFixed(2)} 分，请重新分配`);
      } else {
        alert(`还剩 ${remainingAmount.toFixed(2)} 分 未分配，必须全部分配完才能确认`);
      }
      return;
    }

    // 检查是否有分配
    const hasAllocation = Object.values(rewardAmounts).some(v => v > 0);
    if (!hasAllocation) {
      alert('请至少给一位回复者分配积分');
      return;
    }

    if (!window.confirm('确定分配积分吗？此操作不可撤销。')) return;

    try {
      const token = localStorage.getItem('access_token');
      // 批量发放赏金 - 按用户分配
      const allocations = Object.entries(rewardAmounts)
        .filter(([_, amount]) => amount > 0)
        .map(([userId, amount]) => ({
          user_id: parseInt(userId),
          amount: parseFloat(amount)
        }));

      // 根据业务类型调用不同的API
      let apiUrl;
      switch (bizType) {
        case 'liuyao':
          apiUrl = `/api/liuyao/${chartId}/grant-rewards`;
          break;
        case 'qimen':
          apiUrl = `/api/qimen/${chartId}/grant-rewards`;
          break;
        case 'bazi':
          apiUrl = `/api/bazi/${chartId}/grant-rewards`;
          break;
        default:
          throw new Error(`不支持的业务类型: ${bizType}`);
      }

      const response = await axios.post(apiUrl,
        { allocations },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        alert('积分发放成功！');
        fetchRewardInfo();
      } else {
        alert('操作失败: ' + response.data.message);
      }
    } catch (error) {
      alert('操作失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 取消悬赏
  const handleCancelReward = async () => {
    if (!window.confirm('确定要取消悬赏吗？积分将退还至您的账户。')) return;

    try {
      const token = localStorage.getItem('access_token');

      // 根据业务类型调用不同的API
      let apiUrl;
      switch (bizType) {
        case 'liuyao':
          apiUrl = `/api/liuyao/${chartId}/cancel-reward`;
          break;
        case 'qimen':
          apiUrl = `/api/qimen/${chartId}/cancel-reward`;
          break;
        case 'bazi':
          apiUrl = `/api/bazi/${chartId}/cancel-reward`;
          break;
        default:
          throw new Error(`不支持的业务类型: ${bizType}`);
      }

      const response = await axios.post(apiUrl, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        alert('已取消悬赏，积分已退还');
        onClose();
      } else {
        alert('操作失败: ' + response.data.message);
      }
    } catch (error) {
      alert('操作失败: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
        {/* 头部 */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">积分管理</h2>
              <p className="text-orange-100 text-sm">悬赏积分: {paymentAmount} 分</p>
            </div>
          </div>
        </div>

        {/* 内容 */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <>
              {/* 状态信息 */}
              <div className="mb-6 p-4 rounded-xl bg-gray-50">
                <div className="flex items-center justify-between text-gray-700">
                  <div className="flex items-center gap-2">
                    {isFinished ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-orange-500" />
                    )}
                    <span className="font-medium">
                      {isFinished ? '悬赏已结束' :
                       comments.length > 0 ? '悬赏进行中' : '等待回复'}
                    </span>
                  </div>
                  {isFinished && (
                    <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded">
                      已完成
                    </span>
                  )}
                </div>
                {/* 金额分配状态 */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">悬赏总额:</span>
                    <span className="font-medium text-gray-900">{paymentAmount} 分</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">已分配:</span>
                    <span className={`font-medium ${isOverLimit ? 'text-red-600' : allocatedTotal > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                      {allocatedTotal.toFixed(2)} 分
                      {isOverLimit && <span className="ml-1 text-xs">(超出!)</span>}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">剩余可分配:</span>
                    <span className={`font-medium ${remainingAmount < 0 ? 'text-red-600' : remainingAmount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {remainingAmount.toFixed(2)} 分
                    </span>
                  </div>
                  {/* 进度条 */}
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${isOverLimit ? 'bg-red-500' : 'bg-gradient-to-r from-orange-400 to-amber-500'}`}
                      style={{ width: `${Math.min((allocatedTotal / paymentAmount) * 100, 100)}%` }}
                    />
                  </div>
                  {/* 提示信息 */}
                  {isOverLimit && (
                    <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      分配积分超出悬赏总额，请重新分配
                    </p>
                  )}
                  {!isOverLimit && remainingAmount > 0.01 && comments.length > 0 && (
                    <p className="mt-2 text-xs text-orange-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      需要将全部积分分配完才能确认
                    </p>
                  )}
                </div>
              </div>

              {/* 用户回复列表 */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  回复用户 ({userGroups.length})
                </h3>
                {userGroups.length > 0 ? (
                  <div className="space-y-3">
                    {userGroups.map((user) => (
                      <div key={user.user_id} className="p-4 border border-gray-100 rounded-xl hover:border-orange-200 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-medium">
                              {user.user_nickname?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{user.user_nickname}</p>
                              <p className="text-xs text-gray-400">
                                {user.comments.length} 条回复
                              </p>
                            </div>
                          </div>
                          {user.is_confirmed && (
                            <span className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg">
                              已获得 {user.total_reward.toFixed(2)} 分
                            </span>
                          )}
                        </div>
                        {/* 显示该用户的所有评论 */}
                        <div className="mt-3 space-y-2">
                          {user.comments.map((comment) => (
                            <div key={comment.id} className="pl-4 border-l-2 border-gray-200">
                              <p className="text-sm text-gray-600">
                                {comment.content}
                              </p>
                              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {comment.comment_time ? new Date(comment.comment_time).toLocaleString('zh-CN') : '—'}
                              </p>
                            </div>
                          ))}
                        </div>
                        {/* 金额分配区域 */}
                        {!user.is_confirmed && !isFinished && (
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                            <div className="flex-1 flex items-center gap-2">
                              <span className="text-sm text-gray-500">分配积分:</span>
                              <div className="relative flex-1 max-w-[120px]">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">分</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={rewardAmounts[user.user_id] || ''}
                                  onChange={(e) => handleAmountChange(user.user_id, e.target.value)}
                                  className={`w-full pl-7 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                                    isOverLimit ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                  }`}
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>暂无回复</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 底部操作 */}
        <div className="border-t border-gray-100 p-4 bg-gray-50 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            关闭
          </button>
          <div className="flex gap-2">
            {comments.length === 0 && !isFinished && (
              <button
                onClick={handleCancelReward}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                取消悬赏
              </button>
            )}
            {comments.length > 0 && !isFinished && (
              <button
                onClick={handleGrantAllRewards}
                disabled={!isExactMatch}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isExactMatch
                    ? 'text-white bg-orange-500 hover:bg-orange-600'
                    : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                确认分配
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardManagementModal;